// Step 6: the model. Its bands against the DFT bands along the path, the spreads and centres of the Wannier
// functions, how fast the hoppings decay, the warnings of the quality report; more iterations from the .chk, the model
// again with a cut-off, the files; "Use in NEGF Transport" and "Continue in Properties" (new projects with a copy of
// the model).
import { Alert, Badge, Button, Group, Loader, NumberInput, SegmentedControl, SimpleGrid, Stack, Table, Tabs, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ErrorBox, notifyError } from "../../components/Errors";
import { CancelButton, JobProgress, JobStatusBadge } from "../../components/Jobs";
import { Plot } from "../../components/Plot";
import { LatticeBox, Legend } from "../../components/Lattice";
import type { DrawAtom } from "../../lib/lattice";
import { download, post, type Project, type ProjectDetail } from "../../lib/api";
import { useLiveJob, useMe } from "../../lib/hooks";
import { convergenceFigure, decayFigure, fmtMeV, modelBandsFigure, submitWannier, wannierBands, wannierReport, type WannierChannel,
  type WannierMemory } from "../../lib/wannier";
import type { Remember } from "./WannierWorkspace";
import { LiveSpread } from "./RunStep";

const fileOrder = (n: string) => [".win", ".wout", "_hr.dat", "_model.json", "wannier_run.zip"].findIndex((s) => n.endsWith(s));

export function ResultsStep({ project, mem, remember }: { project: ProjectDetail; mem: WannierMemory; remember: Remember }) {
  const { job } = useLiveJob(mem.job);
  const done = job?.status === "succeeded";
  const isModel = job?.kind === "wannier.model";
  const report = useQuery({ queryKey: ["wannier-report", mem.job], queryFn: () => wannierReport(mem.job!), enabled: done && !isModel });
  const hasBands = !!job?.result?.bands_file;
  const bands = useQuery({ queryKey: ["wannier-bands", mem.job], queryFn: () => wannierBands(mem.job!), enabled: done && hasBands });
  const [seed, setSeed] = useState<string | null>(null);
  if (!job) return <Loader size="sm" />;
  if (!done)
    return (
      <Stack gap={4} mt="md">
        <Group gap="xs"><JobStatusBadge status={job.status} /><Text size="sm">{job.kind === "wannier.model" ? "Building the model" : "Wannier90"}</Text>
          <CancelButton job={job} /></Group>
        <JobProgress job={job} />
        <LiveSpread job={job} />
        {job.error && <ErrorBox error={new Error(job.error)} />}
      </Stack>
    );
  const r = job.result ?? {};
  const seeds: string[] = r.seeds ?? [];
  const current = seed && seeds.includes(seed) ? seed : seeds[0];
  const ch: WannierChannel | undefined = current ? report.data?.channels[current] : undefined;

  return (
    <Stack gap="md" mt="md" data-testid="wannier-results">
      {isModel ? <ModelOnly result={r} /> : (
        <>
          <Group gap="xs">
            <Badge variant="light">route {r.route}</Badge>
            <Badge variant="light">{r.num_wann} Wannier functions{seeds.length > 1 ? " per spin" : ""}</Badge>
            {r.spin === "spinor" && <Badge variant="light" color={r.spin_matrices ? "green" : "orange"} data-testid="spin-matrices">
              {r.spin_matrices ? "spinors · spin matrices σ(R)" : "spinors · no spin matrices"}</Badge>}
            {r.disentanglement?.method === "projectability" && <Badge variant="light" color="grape" data-testid="disentanglement">
              projectability disentanglement (frozen ≥ {r.disentanglement.max}, out &lt; {r.disentanglement.min})</Badge>}
            {r.disentanglement?.method === "scdm" && <Badge variant="light" color="grape" data-testid="disentanglement">
              SCDM projections ({r.disentanglement.entanglement})</Badge>}
            {r.wannier90_version && <Badge variant="outline">Wannier90 {r.wannier90_version}</Badge>}
            {r.density_reused && <Badge variant="outline" color="green">DFT density reused (no new SCF)</Badge>}
            {Object.entries(r.converged ?? {}).map(([s, c]: [string, any]) => (
              <Badge key={s} color={c.localisation && c.disentanglement !== false ? "green" : "orange"} variant="light">
                {seeds.length > 1 ? `${s}: ` : ""}{c.localisation && c.disentanglement !== false ? "converged" : "not converged"}</Badge>
            ))}
          </Group>
          {((r.notes ?? []) as string[]).map((n) => <Text key={n} size="xs" c="dimmed">{n}</Text>)}
          {seeds.length > 1 && <SegmentedControl size="xs" value={current} onChange={setSeed} data={seeds} />}
          {report.error && <ErrorBox error={report.error} />}
          {ch && <QualityLine ch={ch} />}
          {(r.warnings ?? []).length > 0 && (
            <Alert color="orange" p="xs" title="The quality report says" data-testid="wannier-warnings">
              {(r.warnings as string[]).map((w) => <Text key={w} size="xs">{w}</Text>)}
            </Alert>
          )}
          <Tabs defaultValue={hasBands ? "bands" : "spreads"} keepMounted={false}>
            <Tabs.List>
              {hasBands && <Tabs.Tab value="bands">Bands</Tabs.Tab>}
              <Tabs.Tab value="spreads">Spreads and centres</Tabs.Tab>
              <Tabs.Tab value="cell">Centres in the cell</Tabs.Tab>
              <Tabs.Tab value="convergence">Convergence</Tabs.Tab>
              <Tabs.Tab value="decay">Decay</Tabs.Tab>
              <Tabs.Tab value="files">Files</Tabs.Tab>
            </Tabs.List>
            {hasBands && <Tabs.Panel value="bands" pt="sm">
              {bands.data && current ? <Plot {...modelBandsFigure(bands.data, current)} height={440} /> : <Loader size="sm" />}
              {bands.data?.dft.points ? <Text size="xs" c="dimmed" data-testid="route-b-bands">Your interface files have no DFT bands along a path:
                the path is {bands.data.dft.source === "win" ? "the .win's kpoint_path" : "the standard one of the structure"}, and the open circles
                are the .eig's states at the mesh k-points that lie on it - inside the frozen window the model goes through them exactly.</Text>
              : <Text size="xs" c="dimmed">Inside the frozen window (shaded) the model reproduces the DFT bands exactly on the mesh; between
                mesh points the difference shows how well the model interpolates (a finer mesh helps).</Text>}
            </Tabs.Panel>}
            <Tabs.Panel value="spreads" pt="sm">{ch ? <SpreadsTable ch={ch} /> : <Loader size="sm" />}</Tabs.Panel>
            <Tabs.Panel value="cell" pt="sm">
              {ch?.structure ? <CentresView ch={ch} seed={current ?? "wannier"} /> : ch ? <Text size="sm" c="dimmed">No structure in the .wout.</Text> : <Loader size="sm" />}
            </Tabs.Panel>
            <Tabs.Panel value="convergence" pt="sm">
              {ch?.history ? <><Plot {...convergenceFigure(ch.history)} height={340} />
                <Text size="xs" c="dimmed">Disentanglement lowers the gauge-invariant spread Ω_I by choosing the subspace; localisation then lowers
                  the rest (Ω_D + Ω_OD) by choosing the gauge U(k) inside it. Flat curves at the end: converged.</Text></> : <Loader size="sm" />}
            </Tabs.Panel>
            <Tabs.Panel value="decay" pt="sm">
              {ch ? <><Plot {...decayFigure(ch)} height={360} />
                <Text size="xs" c="dimmed">Hoppings above {fmtMeV(ch.decay.cutoff)} reach {ch.decay.range_for_cutoff?.toFixed(2)} Å; at the edge of the
                  Wigner-Seitz supercell they are {fmtMeV(ch.decay.edge_hopping)} (more than 1 meV there: the mesh is too coarse for the model's range).</Text></>
                : <Loader size="sm" />}
            </Tabs.Panel>
            <Tabs.Panel value="files" pt="sm"><FileList files={r.files ?? {}} /></Tabs.Panel>
          </Tabs>
          <Iterate project={project} jobId={job.id} remember={remember} hasChk={Object.keys(r.files ?? {}).some((n) => n.endsWith(".chk"))} />
        </>
      )}
      <Onward jobId={job.id} modelFile={r.model_file} seed={r.seed} zip={(r.files ?? {})["wannier_run.zip"]} />
    </Stack>
  );
}

function QualityLine({ ch }: { ch: WannierChannel }) {
  const sp = ch.spreads ?? { each: [] };
  const e = ch.band_error_path ?? ch.band_error_mesh;
  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} data-testid="quality-line">
      <Stat label="Total spread Ω" value={sp.Total != null ? `${sp.Total.toFixed(3)} Å²` : "–"}
        sub={sp.I != null ? `Ω_I ${sp.I.toFixed(3)} · Ω_D ${(sp.D ?? 0).toFixed(3)} · Ω_OD ${(sp.OD ?? 0).toFixed(3)}` : ""} />
      <Stat label={ch.band_error_path ? "Band error along the path" : "Band error on the mesh"} value={fmtMeV(e?.max)}
        sub={e?.rms != null ? `RMS ${fmtMeV(e.rms)} · ${e.states} states` : ""} />
      <Stat label="Hoppings above 1 meV reach" value={ch.decay.range_for_cutoff != null ? `${ch.decay.range_for_cutoff.toFixed(2)} Å` : "–"}
        sub={`edge: ${fmtMeV(ch.decay.edge_hopping)}`} />
      <Stat label="Imaginary part of H(R)" value={ch.imag_fraction != null ? ch.imag_fraction.toExponential(1) : "–"} sub="of its largest element" />
      {ch.spin_degeneracy?.expected && ch.spin_degeneracy.max_split != null && (
        <Stat label="Kramers pairs split by" value={fmtMeV(ch.spin_degeneracy.max_split)} data-testid="spin-degeneracy"
          sub={`${ch.spin_degeneracy.where === "path" ? "along the path" : "between mesh points"} · RMS ${fmtMeV(ch.spin_degeneracy.rms_split)}`
            + (ch.spin_degeneracy.max_split_window != null ? ` · frozen window ${fmtMeV(ch.spin_degeneracy.max_split_window)}` : "")
            + " (DFT: degenerate)"} />
      )}
    </SimpleGrid>
  );
}

function Stat({ label, value, sub, "data-testid": testId }: { label: string; value: string; sub?: string; "data-testid"?: string }) {
  return (
    <div data-testid={testId}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={600}>{value}</Text>
      {sub && <Text size="xs" c="dimmed">{sub}</Text>}
    </div>
  );
}

function SpreadsTable({ ch }: { ch: WannierChannel }) {
  const each = ch.spreads?.each ?? [];
  return (
    <Table.ScrollContainer minWidth={520}>
      <Table fz="sm" withTableBorder data-testid="spreads-table">
        <Table.Thead><Table.Tr><Table.Th>#</Table.Th><Table.Th>Centre x, y, z (Å)</Table.Th><Table.Th>Spread (Å²)</Table.Th>
          <Table.Th>From the nearest atom (Å)</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {each.map((s, i) => (
            <Table.Tr key={i}>
              <Table.Td>{i + 1}</Table.Td>
              <Table.Td>{(ch.centres[i] ?? []).map((c) => c.toFixed(4)).join(", ")}</Table.Td>
              <Table.Td>{s.toFixed(4)}</Table.Td>
              <Table.Td>{ch.centre_offsets?.[i]?.toFixed(3) ?? "–"}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {ch.shortest_bond != null && <Text size="xs" c="dimmed" mt={4}>Shortest bond {ch.shortest_bond.toFixed(3)} Å: a centre half a bond
        from the atoms sits on the bond (sp³ in silicon), one on an atom is atom-like (d in copper).</Text>}
    </Table.ScrollContainer>
  );
}

function FileList({ files }: { files: Record<string, string> }) {
  const names = Object.keys(files).sort((a, b) => (fileOrder(b) - fileOrder(a)) || a.localeCompare(b));
  return (
    <Group gap="xs">
      {names.map((n) => <Button key={n} size="compact-sm" variant="default"
        onClick={() => download(`/files/${files[n]}/download`, n).catch(notifyError)}>{n}</Button>)}
    </Group>
  );
}

function Iterate({ project, jobId, remember, hasChk }: { project: ProjectDetail; jobId: string; remember: Remember; hasChk: boolean }) {
  const qc = useQueryClient();
  const [n, setN] = useState<number>(500);
  const [cut, setCut] = useState<number>(0.001);
  const go = useMutation({
    mutationFn: (kind: "restart" | "model") => kind === "restart"
      ? submitWannier(project.id, "wannier.restart", { job: jobId, localise: n })
      : submitWannier(project.id, "wannier.model", { job: jobId, hop_cutoff: cut }),
    onSuccess: (j) => { remember({ job: j.id }); qc.invalidateQueries({ queryKey: ["project", project.id] }); },
    onError: (e) => notifyError(e),
  });
  return (
    <Group gap="md" align="flex-end">
      {hasChk && <Group gap="xs" align="flex-end">
        <NumberInput size="xs" w={120} label="More iterations" min={1} max={20000} value={n} onChange={(v) => setN(Math.round(Number(v)) || 1)} />
        <Button size="xs" variant="light" loading={go.isPending && go.variables === "restart"} onClick={() => go.mutate("restart")}>Restart from the .chk</Button>
      </Group>}
      <Group gap="xs" align="flex-end">
        <NumberInput size="xs" w={140} label="Model: drop hoppings below" min={0} step={0.001} decimalScale={6} value={cut}
          rightSection={<Text size="xs" c="dimmed">eV</Text>} onChange={(v) => setCut(Number(v) || 0)} />
        <Button size="xs" variant="light" loading={go.isPending && go.variables === "model"} onClick={() => go.mutate("model")}>Make the model again</Button>
      </Group>
    </Group>
  );
}

function ModelOnly({ result }: { result: Record<string, any> }) {
  return (
    <Table withTableBorder fz="sm" data-testid="model-only">
      <Table.Tbody>
        <Table.Tr><Table.Td>Model</Table.Td><Table.Td>{result.seed} ({result.route === "C" ? "from your Wannier90 files" : "from a run, made again"})</Table.Td></Table.Tr>
        <Table.Tr><Table.Td>Orbitals, sites</Table.Td><Table.Td>{result.num_orbitals} orbitals on {result.sites} sites</Table.Td></Table.Tr>
        <Table.Tr><Table.Td>Hopping blocks</Table.Td><Table.Td>{result.n_hoppings}{result.hop_cutoff ? ` (|H| ≥ ${result.hop_cutoff} eV)` : " (all kept)"}</Table.Td></Table.Tr>
      </Table.Tbody>
    </Table>
  );
}

function Onward({ jobId, modelFile, seed, zip }: { jobId: string; modelFile?: string; seed?: string; zip?: string }) {
  const { data: me } = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const go = useMutation({
    mutationFn: (target: "negf" | "properties") => post<Project>(`/wannier/runs/${jobId}/continue`, { target }),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["me"] }); nav(`/projects/${p.id}`); },
    onError: (e) => notifyError(e),
  });
  const can = (k: string) => !!(me?.limits[`${k}.enabled`]);
  return (
    <Group justify="space-between" data-testid="wannier-onward">
      <Group gap="xs">
        {modelFile && <Button size="xs" variant="default" onClick={() => download(`/files/${modelFile}/download`, `${seed ?? "wannier"}_model.json`).catch(notifyError)}>Model file</Button>}
        {zip && <Button size="xs" variant="default" onClick={() => download(`/files/${zip}/download`, `${seed ?? "wannier"}_wannier_run.zip`).catch(notifyError)}>Download the run (.zip)</Button>}
      </Group>
      <Group gap="xs">
        <Button variant="light" disabled={!modelFile || !can("negf")} loading={go.isPending && go.variables === "negf"}
          onClick={() => go.mutate("negf")} data-testid="use-in-negf">Use in NEGF Transport →</Button>
        <Button disabled={!modelFile || !can("properties")} loading={go.isPending && go.variables === "properties"}
          onClick={() => go.mutate("properties")} data-testid="continue-in-properties">Continue in Properties →</Button>
      </Group>
    </Group>
  );
}

const ATOM_COLORS = ["#7a7a7a", "#2a78d6", "#1baf7a", "#e87ba4", "#4a3aa7"];

/** The atoms of the cell and the Wannier centres (orange, size from the spread). */
function CentresView({ ch, seed }: { ch: WannierChannel; seed: string }) {
  const st = ch.structure!;
  const elements = [...new Set(st.species)];
  const atoms: DrawAtom[] = st.cart.map((p, i) => ({ key: `a${i}`, site: i, cell: [0, 0, 0], pos: p as [number, number, number],
    color: ATOM_COLORS[elements.indexOf(st.species[i]) % ATOM_COLORS.length], radius: 0.9, label: st.species[i] }));
  const spreads = ch.spreads?.each ?? [];
  const centres: DrawAtom[] = ch.centres.map((c, i) => ({ key: `w${i}`, site: st.cart.length + i, cell: [0, 0, 0],
    pos: c as [number, number, number], color: "#eb6834", radius: Math.max(0.25, Math.min(1.2, 0.35 * Math.sqrt(spreads[i] ?? 1))),
    label: `W${i + 1}` }));
  return (
    <LatticeBox atoms={[...atoms, ...centres]} bonds={[]} lattice={st.lattice as [number, number, number][]} pngName={`${seed}_centres`} height={420}
      legend={<Legend items={[...elements.map((e, i) => ({ key: e, label: e, color: ATOM_COLORS[i % ATOM_COLORS.length] })),
        { key: "w", label: "Wannier centre (size ~ √spread)", color: "#eb6834" }]} />}
      tooltip={(a) => a.key.startsWith("w") ? `${a.label}: spread ${(spreads[a.site - st.cart.length] ?? 0).toFixed(3)} Å²` : a.label}
      footer={<Text size="xs" c="dimmed">Centres as Wannier90 left them (not folded into the cell). sp³ centres sit on the bonds, d centres on the atom,
        interstitial ones in the holes.</Text>} />
  );
}
